import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { Reveal, Chapter } from "@/components/motion";

const PHOTOS = [
  { src: IMAGES.workKitchen, alt: "A renovated kitchen unpacked and organized by Porter New Orleans in Uptown", caption: "Renovated kitchen, Uptown" },
  { src: IMAGES.workCloset, alt: "A walk-in closet organized by Porter New Orleans in Uptown New Orleans", caption: "A dream closet, Uptown" },
  { src: IMAGES.workSpices, alt: "Spices decanted into labeled jars by Porter New Orleans", caption: "Spices, decanted & labeled" },
  { src: IMAGES.workBookshelf, alt: "A navy built-in bookshelf styled by Porter New Orleans in Covington", caption: "Bookshelf refresh, Covington" },
  { src: IMAGES.workPantryWalkin, alt: "A walk-in pantry organized by Porter New Orleans in River Ridge", caption: "Walk-in pantry, River Ridge" },
  { src: IMAGES.workGirlsCloset, alt: "A children's closet organized by Porter New Orleans on St. Charles Avenue", caption: "Little ones' closet, St. Charles Ave" },
  { src: IMAGES.workFridge, alt: "A refrigerator stocked and organized by Porter New Orleans in Uptown", caption: "Stocked fridge, Uptown" },
  { src: IMAGES.workMensCloset, alt: "A men's master closet organized by Porter New Orleans in Uptown", caption: "Men's master closet, Uptown" },
  { src: IMAGES.workPantryLabeled, alt: "Labeled baskets and bins in a pantry organized by Porter New Orleans", caption: "Labeled, loved, easy to keep" },
  { src: IMAGES.workManCave, alt: "Sports memorabilia displayed and organized by Porter New Orleans", caption: "Collections, beautifully displayed" },
  { src: IMAGES.workShoes, alt: "A wall of shoes arranged by Porter New Orleans", caption: "Every pair in its place" },
  { src: IMAGES.workToyBins, alt: "Labeled toy bins organized by Porter New Orleans", caption: "Playroom, finally tamed" },
  { src: IMAGES.workPantryMarigny, alt: "A closet transformed into an organized pantry by Porter in Faubourg Marigny", caption: "Closet turned pantry, Faubourg Marigny" },
  { src: IMAGES.workClosetMandeville, alt: "A master closet organized by Porter New Orleans in Mandeville", caption: "Master closet, Mandeville" },
  { src: IMAGES.workDecanted, alt: "Dry goods decanted into labeled containers by Porter New Orleans", caption: "Drawers, decanted" },
  { src: IMAGES.workPantryLakeview, alt: "Pantry shelves organized by Porter New Orleans in Lakeview", caption: "Pantry refresh, Lakeview" },
  { src: IMAGES.workShoesHisHers, alt: "A his and hers shoe wall organized by Porter New Orleans", caption: "His & hers shoe wall" },
];

export default function Gallery() {
  return (
    <>
      <Seo
        title="Gallery | Porter New Orleans — Life, Organized"
        description="A look inside real Porter New Orleans projects — organized closets, pantries, and living spaces across Greater New Orleans, from Uptown to River Ridge."
        path="/gallery"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 pt-16 sm:pt-24" data-testid="gallery-hero">
        <Reveal y={16}>
          <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-gold-dark">
            <span className="h-px w-8 bg-gold" aria-hidden="true" /> Gallery
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-7 max-w-4xl font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
            This is what <em className="text-gold-dark">life, organized</em> feels like.
          </h1>
          <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-navy/70">
            Real Porter projects from real New Orleans homes — the calm, functional, beautifully
            kept spaces we help our clients create and keep.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 lg:pb-32" data-testid="gallery-grid">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PHOTOS.map((photo, i) => (
            <Reveal key={photo.src} delay={(i % 3) * 0.08}>
              <figure className="group relative overflow-hidden rounded-3xl">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
                <figcaption className="absolute inset-x-4 bottom-4 translate-y-3 rounded-2xl border border-white/40 bg-white/80 px-5 py-3 opacity-0 backdrop-blur-md transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <span className="font-serif italic text-lg text-navy">{photo.caption}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-col items-start justify-between gap-8 rounded-3xl bg-navy px-8 py-12 sm:px-12 lg:flex-row lg:items-center">
            <div>
              <Chapter number="—" label="Your home next" />
              <p className="mt-5 max-w-xl font-serif text-3xl sm:text-4xl leading-snug text-cream">
                Imagine opening the closet door and <em className="text-gold-light">exhaling.</em>
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="gallery-cta-button"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-9 py-4 text-sm font-bold tracking-wide text-navy-dark transition-all duration-300 hover:bg-gold-light hover:-translate-y-0.5"
            >
              Get in Touch <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
